import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} cartDiscountErrorNetwork - The request error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
    'cartDiscountErrorNetwork',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Set<string>} */
  #confirmedCodes = new Set();

  /** @type {Set<string>} */
  #desiredCodes = new Set();

  /** @type {Promise<void> | null} */
  #drainPromise = null;

  /** @type {Map<HTMLInputElement | HTMLButtonElement | HTMLSelectElement | HTMLTextAreaElement, {disabled: boolean, ariaDisabled: string | null}>} */
  #lockedControls = new Map();

  connectedCallback() {
    super.connectedCallback();
    if (!this.#drainPromise) this.#syncCodesFromDOM();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#setBusy(false);
  }

  /**
   * Adds a code to the desired state. A single serial drain owns all network
   * writes, so no request can be aborted by a later click or be calculated from
   * stale pills in the DOM.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;
    if (!this.#drainPromise) this.#syncCodesFromDOM();
    this.#clearDiscountError(this);

    if (this.#hasCode(this.#desiredCodes, discountCodeValue)) {
      this.#showDiscountError(this, 'network', 'Ten kod rabatowy jest już dodany.');
      return;
    }

    this.#desiredCodes.add(discountCodeValue);
    discountCode.value = '';
    await this.#queueDiscountSync();
    cartPerformance.measureFromEvent('discount-update:user-action', event);
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      (!(event instanceof MouseEvent) && !(event instanceof KeyboardEvent)) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    if (!this.#drainPromise) this.#syncCodesFromDOM();
    const matchingCode = this.#findCode(this.#desiredCodes, discountCode);
    if (!matchingCode) return;

    this.#desiredCodes.delete(matchingCode);
    await this.#queueDiscountSync();
  };

  /** @returns {Promise<void>} */
  #queueDiscountSync() {
    if (!this.#drainPromise) {
      this.#drainPromise = this.#drainDiscountQueue().finally(() => {
        this.#drainPromise = null;
      });
    }
    return this.#drainPromise;
  }

  /**
   * Serially reconciles Shopify with the newest desired Set. If another action
   * is queued while fetch/morph is running, the loop sends the next state only
   * after the authoritative response has become the new confirmed state.
   */
  async #drainDiscountQueue() {
    if (typeof this.dataset.sectionId !== 'string') return;

    /** @type {{type: 'discount_code' | 'shipping' | 'network', message?: string} | null} */
    let finalError = null;
    this.#setBusy(true);

    try {
      while (!this.#setsEqual(this.#confirmedCodes, this.#desiredCodes)) {
        const targetCodes = new Set(this.#desiredCodes);
        const addedCodes = Array.from(targetCodes).filter((code) => !this.#hasCode(this.#confirmedCodes, code));
        const abortController = new AbortController();
        this.#activeFetch = abortController;

        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: Array.from(targetCodes).join(','),
            sections: [this.dataset.sectionId],
          }),
        });

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(`Discount update failed with status ${response.status}`);
        if (!Array.isArray(data.discount_codes)) throw new Error('Discount response is missing discount codes');

        const sectionHtml = data.sections?.[this.dataset.sectionId];
        if (typeof sectionHtml !== 'string') throw new Error('Discount response is missing cart section HTML');

        const renderedCodes = this.#discountCodesFromSectionHTML(sectionHtml);
        const invalidAddedCodes = addedCodes.filter(
          (code) => this.#discountApplicability(data.discount_codes, code) === false
        );
        const shippingOnlyCodes = addedCodes.filter(
          (code) =>
            this.#discountApplicability(data.discount_codes, code) === true &&
            !this.#hasCode(renderedCodes, code)
        );

        for (const code of [...invalidAddedCodes, ...shippingOnlyCodes]) {
          const desiredCode = this.#findCode(this.#desiredCodes, code);
          if (desiredCode) this.#desiredCodes.delete(desiredCode);
        }

        if (invalidAddedCodes.length > 0) finalError = { type: 'discount_code' };
        else if (shippingOnlyCodes.length > 0) finalError = { type: 'shipping' };

        document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
        await morphSection(this.dataset.sectionId, sectionHtml);
        this.#confirmedCodes = renderedCodes;
        this.#setBusy(true);
        this.#activeFetch = null;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
      finalError = {
        type: 'network',
        message: 'Nie udało się zaktualizować kodu rabatowego. Sprawdź połączenie i spróbuj ponownie.',
      };
      await this.#recoverDiscountState(finalError.type, finalError.message);
      this.#syncCodesFromDOM();
    } finally {
      this.#activeFetch = null;
      if (finalError) {
        const section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
        const component = section?.querySelector('cart-discount-component');
        this.#showDiscountError(component instanceof HTMLElement ? component : this, finalError.type, finalError.message);
      }
      this.#setBusy(false);
    }
  }

  #syncCodesFromDOM() {
    const codes = new Set(this.#existingDiscounts());
    this.#confirmedCodes = codes;
    this.#desiredCodes = new Set(codes);
  }

  /**
   * @param {string} sectionHtml
   * @returns {Set<string>}
   */
  #discountCodesFromSectionHTML(sectionHtml) {
    const parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
    const codes = new Set();
    for (const pill of section?.querySelectorAll('.cart-discount__pill') || []) {
      if (pill instanceof HTMLLIElement && pill.dataset.discountCode) codes.add(pill.dataset.discountCode);
    }
    return codes;
  }

  /**
   * @param {Array<{code: string, applicable: boolean}>} discounts
   * @param {string} code
   * @returns {boolean | undefined}
   */
  #discountApplicability(discounts, code) {
    return discounts.find((discount) => discount.code.toLocaleLowerCase() === code.toLocaleLowerCase())?.applicable;
  }

  /** @param {Set<string>} codes @param {string} code */
  #findCode(codes, code) {
    const normalisedCode = code.toLocaleLowerCase();
    return Array.from(codes).find((candidate) => candidate.toLocaleLowerCase() === normalisedCode);
  }

  /** @param {Set<string>} codes @param {string} code */
  #hasCode(codes, code) {
    return this.#findCode(codes, code) !== undefined;
  }

  /** @param {Set<string>} left @param {Set<string>} right */
  #setsEqual(left, right) {
    return left.size === right.size && Array.from(left).every((code) => this.#hasCode(right, code));
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    if (busy) {
      this.setAttribute('aria-busy', 'true');
      for (const control of this.querySelectorAll('input, button, select, textarea')) {
        if (
          !(control instanceof HTMLInputElement) &&
          !(control instanceof HTMLButtonElement) &&
          !(control instanceof HTMLSelectElement) &&
          !(control instanceof HTMLTextAreaElement)
        ) continue;
        if (!this.#lockedControls.has(control)) {
          this.#lockedControls.set(control, {
            disabled: control.disabled,
            ariaDisabled: control.getAttribute('aria-disabled'),
          });
        }
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    for (const [control, state] of this.#lockedControls) {
      if (!control.isConnected) continue;
      control.disabled = state.disabled;
      if (state.ariaDisabled === null) control.removeAttribute('aria-disabled');
      else control.setAttribute('aria-disabled', state.ariaDisabled);
    }
    this.#lockedControls.clear();
  }

  /** @param {ParentNode} root */
  #clearDiscountError(root) {
    const container = root.querySelector('[ref="cartDiscountError"]');
    container?.classList.add('hidden');
    for (const error of root.querySelectorAll('.cart-discount__error-text')) error.classList.add('hidden');
  }

  /**
   * @param {ParentNode} root
   * @param {'discount_code' | 'shipping' | 'network'} type
   * @param {string} [message]
   */
  #showDiscountError(root, type, message) {
    this.#clearDiscountError(root);
    const container = root.querySelector('[ref="cartDiscountError"]');
    const ref =
      type === 'discount_code'
        ? 'cartDiscountErrorDiscountCode'
        : type === 'shipping'
          ? 'cartDiscountErrorShipping'
          : 'cartDiscountErrorNetwork';
    const target = root.querySelector(`[ref="${ref}"]`);
    if (!(container instanceof HTMLElement) || !(target instanceof HTMLElement)) return;
    if (message) target.textContent = message;
    const disclosure = target.closest('details');
    if (disclosure instanceof HTMLDetailsElement) disclosure.open = true;
    container.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Re-renders the section to reveal Shopify's confirmed cart/discount state,
   * then places the message in the freshly rendered component.
   * @param {'discount_code' | 'shipping' | 'network'} type
   * @param {string} [message]
   */
  async #recoverDiscountState(type, message) {
    const sectionId = this.dataset.sectionId;
    if (typeof sectionId !== 'string') return;

    try {
      await sectionRenderer.renderSection(sectionId, { cache: false });
    } catch (recoveryError) {
      console.error('Unable to refresh discount state after an update error', recoveryError);
    }

    const section = document.getElementById(`shopify-section-${sectionId}`);
    const component = section?.querySelector('cart-discount-component');
    this.#showDiscountError(component instanceof HTMLElement ? component : this, type, message);
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
